import {
	type AiKnowledgeCollection,
	type DepartmentSummary,
	type TenantMemberSummary,
} from '@livechat/shared';
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
	createAiKnowledgeCollection,
	deleteAiKnowledgeCollection,
	listAiKnowledgeCollections,
	listDepartments,
	listMembers,
	updateAiKnowledgeCollection,
} from '../api.js';
import { KnowledgeSources } from './KnowledgeSources.js';
import { KnowledgeAssignmentPicker } from './KnowledgeAssignmentPicker.js';
import {
	knowledgeCollectionSlug,
	resolveKnowledgeCollection,
} from '../knowledge-collection-location.js';
import { workspaceHref } from '../workspace-location.js';
import { registerWorkspaceHistoryGuard } from '../workspace-history.js';

type Props = {
	tenantId: string;
	onDraftChange?: () => void;
	collectionSlug?: string;
	onCollectionSelected?: (slug?: string, replace?: boolean) => void;
	onCollectionNameChange?: (name?: string, focusHeading?: boolean) => void;
};

// A company switch must discard the old company's selection and pending UI requests.
export function TenantKnowledge(props: Props) {
	return <KnowledgeLibrary key={props.tenantId} {...props} />;
}

function assignmentNames(ids: string[], options: { id: string; name: string }[]): string {
	return (
		ids
			.map(
				(id) =>
					options.find((option) => option.id === id)?.name ?? 'Unavailable assignment',
			)
			.join(', ') || 'None'
	);
}

function KnowledgeLibrary({
	tenantId,
	onDraftChange,
	collectionSlug,
	onCollectionSelected,
	onCollectionNameChange,
}: Props) {
	const [collections, setCollections] = useState<AiKnowledgeCollection[]>([]);
	const [members, setMembers] = useState<TenantMemberSummary[]>([]);
	const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
	const [localSlug, setLocalSlug] = useState<string>();
	const slug = onCollectionSelected ? collectionSlug : localSlug;
	const selection = slug === 'new' ? 'new' : resolveKnowledgeCollection(slug, collections);
	function selectSlug(value?: string, replace = false): void {
		if (onCollectionSelected) onCollectionSelected(value, replace);
		else setLocalSlug(value);
	}
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();
	const [reload, setReload] = useState(0);
	const [notice, setNotice] = useState<string>();
	const heading = useRef<HTMLHeadingElement>(null);
	const announcedSelection = useRef<string | undefined>(undefined);
	const aiAgents = useMemo(
		() => members.filter((member) => member.memberType === 'ai_agent'),
		[members],
	);

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		void Promise.all([
			listAiKnowledgeCollections(tenantId),
			listMembers(tenantId),
			listDepartments(tenantId),
		])
			.then(([loadedCollections, loadedMembers, loadedDepartments]) => {
				if (!current) return;
				setCollections(loadedCollections);
				setMembers(loadedMembers);
				setDepartments(loadedDepartments);
			})
			.catch((caught: unknown) => {
				if (current)
					setError(
						caught instanceof Error
							? caught.message
							: 'Could not load company knowledge',
					);
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [reload, tenantId]);

	useEffect(() => {
		if (!selection && !loading) heading.current?.focus();
	}, [selection, loading]);

	function backToCollections(message?: string): void {
		selectSlug(undefined);
		setNotice(message);
		// Refresh counts after ingestion/deletion without resetting an open settings draft.
		setReload((value) => value + 1);
	}

	const selected = collections.find((collection) => collection.id === selection);
	useEffect(() => {
		const focusHeading = Boolean(selection) && announcedSelection.current !== selection;
		announcedSelection.current = selection;
		onCollectionNameChange?.(
			selection === 'new' ? 'New collection' : selected?.name,
			focusHeading,
		);
	}, [onCollectionNameChange, selected?.name, selection]);
	if (!loading && !error && (selected || selection === 'new')) {
		return (
			<CollectionWorkspace
				key={selection}
				tenantId={tenantId}
				collection={selected}
				aiAgents={aiAgents}
				departments={departments}
				onBack={backToCollections}
				onSaved={(saved) => {
					const updated = selected
						? collections.map((collection) =>
								collection.id === saved.id ? saved : collection,
							)
						: [...collections, saved];
					setCollections(updated);
					selectSlug(knowledgeCollectionSlug(saved, updated), true);
					// The AI version contains assignments, not collection metadata or
					// unassigned collections. Do not advertise a non-existent publish change.
					if (
						!sameIds(selected?.agentUserIds ?? [], saved.agentUserIds) ||
						!sameIds(selected?.departmentIds ?? [], saved.departmentIds)
					)
						onDraftChange?.();
				}}
				onDeleted={() => {
					if (selected?.agentUserIds.length || selected?.departmentIds.length) {
						onDraftChange?.();
					}
					backToCollections('Collection deleted.');
				}}
			/>
		);
	}

	return (
		<section className="tenant-knowledge-section" aria-labelledby="company-knowledge-heading">
			<section className="settings-card integration-panel">
				<div className="knowledge-toolbar">
					<div className="integration-panel-heading">
						<h4
							className="panel-title"
							id="company-knowledge-heading"
							ref={heading}
							tabIndex={-1}
						>
							Knowledge collections
						</h4>
						<p>
							Open a collection to manage its sources and choose which AI agents or
							departments can use them.
						</p>
					</div>
					<button
						className="primary-button"
						type="button"
						disabled={loading || Boolean(error)}
						onClick={() => {
							setNotice(undefined);
							selectSlug('new');
						}}
					>
						New collection
					</button>
				</div>
				{notice && (
					<div className="alert success" role="status">
						{notice}
					</div>
				)}
				{loading ? (
					<p className="empty-configuration">Loading collections.</p>
				) : error ? (
					<div className="alert error" role="alert">
						{error}{' '}
						<button
							className="secondary-button"
							type="button"
							onClick={() => setReload((value) => value + 1)}
						>
							Retry
						</button>
					</div>
				) : slug ? (
					<div className="alert error" role="alert">
						Collection not found. This link may be outdated or ambiguous, or the
						collection may belong to another company.
					</div>
				) : collections.length ? (
					<ul className="knowledge-collection-list">
						{collections.map((collection) => (
							<li key={collection.id}>
								<div className="knowledge-collection-summary">
									<a
										className="knowledge-collection-link"
										href={workspaceHref({
											tab: 'settings',
											settingsSection: 'ai-knowledge',
											knowledgeCollectionSlug: knowledgeCollectionSlug(
												collection,
												collections,
											),
										})}
										onClick={(event) => {
											if (
												event.button !== 0 ||
												event.metaKey ||
												event.ctrlKey ||
												event.shiftKey ||
												event.altKey
											)
												return;
											event.preventDefault();
											selectSlug(
												knowledgeCollectionSlug(collection, collections),
											);
										}}
									>
										{collection.name}
										<span aria-hidden="true"> →</span>
									</a>
									{collection.description && <p>{collection.description}</p>}
									<span>
										{collection.sourceCount}{' '}
										{collection.sourceCount === 1 ? 'source' : 'sources'} ·{' '}
										{collection.chunkCount} searchable chunks
									</span>
								</div>
								<dl className="knowledge-assignment-summary">
									<div>
										<dt>AI agents</dt>
										<dd>
											{assignmentNames(
												collection.agentUserIds,
												aiAgents.map((agent) => ({
													id: agent.id,
													name: agent.displayName,
												})),
											)}
										</dd>
									</div>
									<div>
										<dt>Departments</dt>
										<dd>
											{assignmentNames(collection.departmentIds, departments)}
										</dd>
									</div>
								</dl>
								{!collection.agentUserIds.length &&
									!collection.departmentIds.length && (
										<p className="knowledge-unassigned">Not assigned yet</p>
									)}
							</li>
						))}
					</ul>
				) : (
					<p className="empty-configuration">
						No collections yet. Create one, choose who can use it, then add your
						knowledge sources.
					</p>
				)}
			</section>
		</section>
	);
}

function sameIds(left: string[], right: string[]): boolean {
	return left.length === right.length && left.every((id) => right.includes(id));
}

function CollectionWorkspace({
	tenantId,
	collection,
	aiAgents,
	departments,
	onBack,
	onSaved,
	onDeleted,
}: {
	tenantId: string;
	collection?: AiKnowledgeCollection;
	aiAgents: TenantMemberSummary[];
	departments: DepartmentSummary[];
	onBack: () => void;
	onSaved: (collection: AiKnowledgeCollection) => void;
	onDeleted: () => void;
}) {
	const [name, setName] = useState(collection?.name ?? '');
	const [description, setDescription] = useState(collection?.description ?? '');
	const [agentUserIds, setAgentUserIds] = useState<string[]>(collection?.agentUserIds ?? []);
	const [departmentIds, setDepartmentIds] = useState<string[]>(collection?.departmentIds ?? []);
	const [busy, setBusy] = useState(false);
	const [sourceBusy, setSourceBusy] = useState(false);
	const [sourceDirty, setSourceDirty] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();
	const mounted = useRef(true);
	const feedback = useRef<HTMLDivElement>(null);
	const locked = busy || sourceBusy;
	const settingsDirty =
		name !== (collection?.name ?? '') ||
		description !== (collection?.description ?? '') ||
		!sameIds(agentUserIds, collection?.agentUserIds ?? []) ||
		!sameIds(departmentIds, collection?.departmentIds ?? []);
	const dirty = settingsDirty || sourceDirty;

	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	useEffect(() => {
		if (error || message) feedback.current?.focus();
	}, [error, message]);
	useEffect(() => {
		if (!dirty && !locked) return;
		const warn = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = '';
		};
		window.addEventListener('beforeunload', warn);
		return () => window.removeEventListener('beforeunload', warn);
	}, [dirty, locked]);
	useEffect(
		() =>
			registerWorkspaceHistoryGuard(
				() => !locked && (!dirty || window.confirm('Discard unsaved collection changes?')),
			),
		[dirty, locked],
	);

	function goBack(): void {
		if (locked || (dirty && !window.confirm('Discard unsaved collection changes?'))) return;
		onBack();
	}

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (locked) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const input = { name, description, agentUserIds, departmentIds };
			const saved = collection
				? await updateAiKnowledgeCollection(tenantId, collection.id, input)
				: await createAiKnowledgeCollection(tenantId, input);
			if (!mounted.current) return;
			setName(saved.name);
			setDescription(saved.description);
			setAgentUserIds(saved.agentUserIds);
			setDepartmentIds(saved.departmentIds);
			onSaved(saved);
			const assignmentsChanged =
				!sameIds(collection?.agentUserIds ?? [], saved.agentUserIds) ||
				!sameIds(collection?.departmentIds ?? [], saved.departmentIds);
			setMessage(
				assignmentsChanged
					? 'Collection saved. Assignment changes are in the AI Draft; publish from Overview & Publish when ready.'
					: 'Collection details saved.',
			);
		} catch (caught) {
			if (mounted.current)
				setError(caught instanceof Error ? caught.message : 'Could not save collection');
		} finally {
			if (mounted.current) setBusy(false);
		}
	}

	async function remove(): Promise<void> {
		if (
			!collection ||
			locked ||
			!window.confirm(
				`Delete "${collection.name}" and all its sources? This cannot be undone.`,
			)
		)
			return;
		setBusy(true);
		setError(undefined);
		try {
			await deleteAiKnowledgeCollection(tenantId, collection.id);
			if (mounted.current) onDeleted();
		} catch (caught) {
			if (mounted.current)
				setError(caught instanceof Error ? caught.message : 'Could not delete collection');
		} finally {
			if (mounted.current) setBusy(false);
		}
	}

	return (
		<section
			className="tenant-knowledge-section"
			aria-label={collection?.name ?? 'New collection'}
		>
			<form
				id="knowledge-collection-settings"
				className="settings-card integration-panel knowledge-details-form"
				onSubmit={(event) => void save(event)}
			>
				<div className="integration-panel-heading">
					<h4 className="panel-title" id="collection-details-heading">
						Details
					</h4>
					<p>
						{collection
							? 'Rename this collection or update its description.'
							: 'Name and describe this collection before adding sources.'}
					</p>
				</div>
				<fieldset className="knowledge-form-fields" disabled={locked}>
					<div className="settings-grid knowledge-settings-grid">
						<label>
							<span>Collection name</span>
							<input
								name="name"
								maxLength={160}
								required
								value={name}
								onChange={(event) => {
									setName(event.currentTarget.value);
									setMessage(undefined);
								}}
							/>
						</label>
						<label>
							<span>Description (optional)</span>
							<input
								name="description"
								maxLength={2000}
								value={description}
								onChange={(event) => {
									setDescription(event.currentTarget.value);
									setMessage(undefined);
								}}
							/>
						</label>
					</div>
				</fieldset>
			</form>
			{collection && (
				<KnowledgeSources
					collection={collection}
					tenantId={tenantId}
					active={true}
					disabled={busy}
					dirty={sourceDirty}
					onDirtyChange={setSourceDirty}
					onBusyChange={setSourceBusy}
				/>
			)}
			<section
				className="settings-card integration-panel knowledge-settings-form"
				aria-labelledby="collection-assignments-heading"
			>
				<div className="integration-panel-heading">
					<h4 className="panel-title" id="collection-assignments-heading">
						Assignments
					</h4>
					<p>Choose which AI agents and departments can use this collection.</p>
				</div>
				<fieldset className="knowledge-form-fields" disabled={locked}>
					<div className="settings-grid knowledge-settings-grid">
						<KnowledgeAssignmentPicker
							label="AI agents"
							options={aiAgents.map((agent) => ({
								id: agent.id,
								name: agent.displayName,
							}))}
							selected={agentUserIds}
							disabled={locked}
							onChange={(values) => {
								setAgentUserIds(values);
								setMessage(undefined);
							}}
						/>
						<KnowledgeAssignmentPicker
							label="Departments"
							options={departments}
							selected={departmentIds}
							disabled={locked}
							onChange={(values) => {
								setDepartmentIds(values);
								setMessage(undefined);
							}}
						/>
					</div>
				</fieldset>
				<p className="knowledge-assignment-note">
					Save assignments to the AI Draft; publish from Overview &amp; Publish. Source
					changes update the library directly.
				</p>
			</section>
			{error && (
				<div
					className="knowledge-collection-feedback alert error"
					ref={feedback}
					role="alert"
					tabIndex={-1}
				>
					{error}
				</div>
			)}
			{message && (
				<div
					className="knowledge-collection-feedback alert success"
					ref={feedback}
					role="status"
					tabIndex={-1}
				>
					{message}
				</div>
			)}
			<div className="knowledge-collection-actions">
				{collection ? (
					<button
						className="danger-button"
						type="button"
						disabled={locked}
						onClick={() => void remove()}
					>
						Delete collection
					</button>
				) : (
					<button
						className="secondary-button"
						type="button"
						disabled={locked}
						onClick={goBack}
					>
						Cancel
					</button>
				)}
				<button
					className="primary-button"
					type="submit"
					form="knowledge-collection-settings"
					disabled={locked || (Boolean(collection) && !settingsDirty)}
				>
					{busy ? 'Saving…' : collection ? 'Save collection' : 'Create collection'}
				</button>
			</div>
		</section>
	);
}
